/**
 * Routine Finder — Supabase Integration
 * Exposes: window.rfSupabase
 *   .saveQuizCompletion(data) — fire-and-forget (legacy Firebase), never throws
 *   .getQuizByToken(token)    → Object | null
 *   .startSession()           → { session_id } | null
 *   .updateSession(sessionId, fields) — fire-and-forget
 *   .trackEvent(sessionId, eventType, detail) — fire-and-forget
 */
(function () {
  'use strict';

  const PROXY_URL        = 'https://ejhsttpvnyqmeziyrvjn.supabase.co/functions/v1';
  const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVqaHN0dHB2bnlxbWV6aXlydmpuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTIwNzU2OTUsImV4cCI6MjA2NzY1MTY5NX0.NjopauGkpEbnJNw8GEVG-t8zCzBHrUyNGg-bmpNwhEA';

  const _headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    'apikey': SUPABASE_ANON_KEY,
  };

  /** POST helper — returns parsed JSON or null on failure */
  async function _post(endpoint, body) {
    try {
      const res = await fetch(`${PROXY_URL}/${endpoint}`, {
        method:  'POST',
        headers: _headers,
        body:    JSON.stringify(body),
      });
      if (!res.ok) return null;
      return res.json();
    } catch (_) {
      return null;
    }
  }

  // ─── Legacy: Firebase quiz completion (kept for backward-compat) ──────
  async function saveQuizCompletion(data) {
    try {
      await fetch(`${PROXY_URL}/save-quiz-data-function`, {
        method:  'POST',
        headers: _headers,
        body:    JSON.stringify(data),
      });
    } catch (_) { /* non-critical */ }
  }

  /** Load a previously saved routine by share token. Returns null on failure. */
  async function getQuizByToken(token) {
    try {
      const res = await fetch(
        `${PROXY_URL}/get-quiz-results?token=${encodeURIComponent(token)}`,
        { headers: _headers }
      );
      if (!res.ok) return null;
      return res.json();
    } catch (_) {
      return null;
    }
  }

  // ─── Tracking: Supabase PostgreSQL via rf-tracking edge function ──────

  /** Create a new quiz session. Returns { session_id } or null. */
  async function startSession() {
    return _post('rf-tracking', {
      type:       'session_start',
      user_agent: navigator.userAgent,
      referrer:   document.referrer || null,
    });
  }

  /** Update an existing session — fire-and-forget, never throws. */
  async function updateSession(sessionId, fields) {
    if (!sessionId) return;
    _post('rf-tracking', {
      type:       'session_update',
      session_id: sessionId,
      ...fields,
    });
  }

  /**
   * Complete a quiz session and return the response.
   * Unlike updateSession this is awaitable and returns the server response,
   * which includes `share_token` when `completed: true` is passed.
   * Returns null on network failure or missing sessionId.
   */
  async function completeSession(sessionId, fields) {
    if (!sessionId) return null;
    return _post('rf-tracking', {
      type:       'session_update',
      session_id: sessionId,
      ...fields,
    });
  }

  /** Track a discrete event — fire-and-forget, never throws. */
  async function trackEvent(sessionId, eventType, detail) {
    _post('rf-tracking', {
      type:       'event',
      session_id: sessionId || null,
      event_type: eventType,
      product_id: detail?.product_id || null,
      variant_id: detail?.variant_id || null,
      metadata:   detail?.metadata   || null,
    });
  }

  window.rfSupabase = {
    saveQuizCompletion,
    getQuizByToken,
    startSession,
    updateSession,
    completeSession,
    trackEvent,
  };
})();
